import { useQueryClient } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { getFetcher } from 'src/core/api/fetcher';
import { logger } from 'src/utils/datadog-log-wrapper';

import { GET_BY_IDS_EXPENSE_ACCOUNTS } from './queries';
import { expenseAccountToLabel } from './reshaper';

type RawExpenseAccount = {
  id: string;
  name: string;
  code: string;
  isArchived: boolean;
  isDefault: boolean;
  isAvailable?: boolean;
};

type ExpenseAccount = {
  id: string;
  name: string;
  isArchived: boolean;
};

export type LoadExpenseAccountsByIdsRawResponse = {
  company: {
    chartOfAccounts: {
      expenseAccounts: { edges: { node: RawExpenseAccount }[] };
    };
  };
};

export type LoadExpenseAccountsByIdsVariables = {
  ids: string[];
};

export const useLoadExpenseAccountsByIds = (includeArchived = false) => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return async (ids: string[]): Promise<ExpenseAccount[]> => {
    if (!ids.length) {
      return [];
    }

    const data = await queryClient.fetchQuery(
      ['useLoadExpenseAccountsByIds', companyId, ids, includeArchived],
      getFetcher<LoadExpenseAccountsByIdsRawResponse>({
        companyId,
        getRequest: () => ({
          type: 'graphQL',
          target: 'v2',
          query: GET_BY_IDS_EXPENSE_ACCOUNTS,
          variables: {
            ids,
            includeArchived,
          },
          method: 'get',
        }),
      }),
      {
        staleTime: 1000 * 60 * 10,
        cacheTime: 1000 * 60 * 10,
      },
    );

    return reshapeFilterValues(
      // data.company.chartOfAccounts.expenseAccounts.edges,
      // TODO: check if there is any profit
      // handles scenario when ids is an empty array
      data.company?.chartOfAccounts?.expenseAccounts?.edges || [],
      includeArchived,
    );
  };
};

/**
 * Reshapers
 */

const reshapeFilterValues = (
  edges: {
    node: RawExpenseAccount;
  }[],
  includeArchived = false,
): ExpenseAccount[] => {
  if (includeArchived) {
    return edges.map(({ node }) => ({
      id: node.id,
      name: expenseAccountToLabel(node),
      isArchived: node.isArchived,
    }));
  }
  return edges
    .map(({ node }) => {
      if (node.isArchived) {
        logger.debug('Called expense account that is', {
          team: 'finance-accountant',
          scope: 'payables::fetch-expense-account',
          node,
        });
      }
      return node.isArchived
        ? null
        : {
            id: node.id,
            name: expenseAccountToLabel(node),
          };
    })
    .filter((item): item is ExpenseAccount => item !== null);
};
